import { useState } from 'react';
import api from '../api/axios';

/**
 * SetPricePanel
 * Drop this in any admin detail page (ITR, GST, DSC, GST Returns).
 *
 * Props:
 *   serviceType  — "ITR" | "GST_REG" | "GST_RETURN" | "DSC"
 *   serviceId    — application ID
 *   serviceDesc  — e.g. "ITR Filing #42 — AY 2024-25"
 *   userId       — user who submitted the application
 *   payment      — existing payment object (or null)
 *   onUpdated    — callback after price is set
 */
export default function SetPricePanel({ serviceType, serviceId, serviceDesc, userId, payment, onUpdated }) {
  const [amount, setAmount] = useState(payment?.amount || '');
  const [note,   setNote]   = useState(payment?.adminNote || '');
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  const gst   = amount ? Math.round(Number(amount) * 0.18 * 100) / 100 : 0;
  const total = amount ? (Number(amount) + gst).toFixed(2) : 0;

  const STATUS_STYLE = {
    PENDING:   { label: 'Payment Pending',  icon: '⏳', bg: '#FFF8E1', color: '#5D4037' },
    INITIATED: { label: 'Payment Initiated',icon: '🔄', bg: '#EBF2FC', color: '#1A5FB4' },
    PAID:      { label: 'Paid ✅',           icon: '✅', bg: '#EAF5EF', color: '#1A6B43' },
    FAILED:    { label: 'Payment Failed',   icon: '❌', bg: '#FDEDEC', color: '#C0392B' },
    REFUNDED:  { label: 'Refunded',         icon: '↩️', bg: '#F5F5F5', color: '#666'    },
  };

  const s = payment ? (STATUS_STYLE[payment.status] || {}) : null;

  const save = async () => {
    if (!amount || Number(amount) < 1) { alert('Enter a valid amount'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/api/payment/set-price/${serviceType}/${serviceId}?serviceDesc=${encodeURIComponent(serviceDesc)}&userId=${userId}`,
        { amount: Number(amount), note }
      );
      setMsg({ type: 'success', text: `Price set: ₹${data.totalAmount} (incl. GST). User will see payment button on dashboard.` });
      if (onUpdated) onUpdated(data);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to set price' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  return (
    <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--creamd)' }}>
      <div style={{ fontWeight: 600, fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', marginBottom: '1rem' }}>
        💳 Payment
      </div>

      {msg && (
        <div className={`alert alert-${msg.type}`} style={{ fontSize: '.82rem', marginBottom: '1rem' }}>
          {msg.text}
        </div>
      )}

      {/* Existing payment status */}
      {payment && s && (
        <div style={{ background: s.bg, border: `1px solid ${s.color}30`, borderRadius: 10, padding: '.75rem 1rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: '.85rem', color: s.color }}>{s.icon} {s.label}</div>
          {payment.invoiceNumber && (
            <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: '.25rem' }}>
              Invoice: {payment.invoiceNumber}
            </div>
          )}
          {payment.status === 'PAID' && (
            <div style={{ fontSize: '.78rem', color: 'var(--green)', marginTop: '.25rem' }}>
              Razorpay ID: {payment.razorpayPaymentId}<br />
              Amount: ₹{payment.totalAmount}
            </div>
          )}
        </div>
      )}

      {/* Don't allow price change if already paid */}
      {payment?.status === 'PAID' ? (
        <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>
          Payment completed — no changes allowed.
        </div>
      ) : (
        <>
          <div className="field" style={{ marginBottom: '.75rem' }}>
            <label>Fee Amount (₹) <span style={{ color: 'var(--muted)', fontSize: '.72rem', fontWeight: 400 }}>excl. GST</span></label>
            <input type="number" min="1" step="1" placeholder="e.g. 499"
              value={amount} onChange={e => setAmount(e.target.value)}
              style={{ maxWidth: 180 }} />
            {amount > 0 && (
              <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: '.3rem' }}>
                + GST (18%): ₹{gst} = <strong style={{ color: 'var(--navy)' }}>Total ₹{total}</strong>
              </div>
            )}
          </div>

          <div className="field" style={{ marginBottom: '1rem' }}>
            <label>Note to User <span style={{ color: 'var(--muted)', fontSize: '.72rem', fontWeight: 400 }}>optional</span></label>
            <input placeholder="e.g. Basic ITR filing fee for AY 2024-25"
              value={note} onChange={e => setNote(e.target.value)} />
          </div>

          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : payment ? '💾 Update Price' : '💳 Set Price & Notify User'}
          </button>
        </>
      )}
    </div>
  );
}
